import { Stripe } from 'stripe';
import {
  BILLING_INTERVALS,
  CARDS_ICONS,
  RecipientDataModel,
} from '../constants';
import { AccountVerificationStatus } from '../components/StripeConnectForm/VerificationStatus';
import { Invoice, InvoiceAdditionalFields } from '../store/payments/types';

/**
 * Determine status of stripe account based on connect status
 * More details about status in stripe docs here: https://stripe.com/docs/api/accounts/object#account_object-requirements
 * @param payouts_enabled Whether Stripe can send payouts to this account.
 * @param accountRequirements
 * @returns
 */
export const GetStripeAccountVerificationStatus = (
  payouts_enabled: boolean,
  accountRequirements: Stripe.Account.Requirements,
) => {
  const { disabled_reason, pending_verification, currently_due } =
    accountRequirements || {};
  let status: AccountVerificationStatus = 'pending';

  if (payouts_enabled) {
    // if payouts enabled we set status to verified
    status = 'verified';
  }

  if (disabled_reason) {
    // if then there is some reason for disabling we know account is in failed state
    status = 'failed';
  }

  if (pending_verification && pending_verification.length > 0) {
    // when there are pending verifiations show pending, this can happen when
    // payouts_enabled or disabled_reason is set
    status = 'pending';
  }

  if (currently_due && currently_due.length > 0) {
    status = 'unverified';
  }

  return status;
};

export const getPaymentMethodLogo = (
  cardBrand: string,
  variant: 'clear' | 'contained' = 'clear',
) => {
  const logoData = CARDS_ICONS[cardBrand];
  // TODO: use variant param once contained icons will be ready
  return logoData ? logoData.path[variant] : '';
};

export interface InvoicePaymentOptions {
  allowPaymentViaACH: boolean;
  allowPaymentViaCC: boolean;
  absorbTransactionFees: boolean;
}

const allowedSourceObjects = ['card', 'bank_account'];

/**
 * Determine if source is one supported and then check if its
 * allowed based on invoice payment options
 * @param source Stripe payment source to check
 * @param paymentOptions invoice payment options setup by sender
 * @returns true/false if allowed
 */
export const isSourceAllowed = (
  source: Stripe.CustomerSource,
  paymentOptions: InvoicePaymentOptions,
) => {
  if (!allowedSourceObjects.includes(source.object)) {
    return false;
  }

  if (paymentOptions.allowPaymentViaCC && source.object === 'card') {
    return true;
  }

  if (paymentOptions.allowPaymentViaACH && source.object === 'bank_account') {
    return true;
  }

  return false;
};

/**
 * Get plan from subscription invoice
 * @param stripe invoice
 * @returns plan for subscription invoice, undefined otherwise
 */
export const getPlanFromInvoice = (info: {
  additionalFields: InvoiceAdditionalFields;
  fields: Invoice;
}) => {
  const { additionalFields, fields } = info;
  const additionalFieldsPlan =
    additionalFields.lines &&
    additionalFields.lines.data.length > 0 &&
    additionalFields.lines.data[0].plan
      ? {
          interval: additionalFields.lines.data[0].plan.interval,
          intervalCount: additionalFields.lines.data[0].plan.interval_count,
        }
      : undefined;
  const fieldsPlan =
    fields.interval && fields.intervalCount
      ? { interval: fields.interval, intervalCount: fields.intervalCount }
      : undefined;

  return additionalFieldsPlan || fieldsPlan;
};

export interface InvoiceDetailOptions {
  id: string;
  amountDue: number;
  ignoreFee: boolean;
  fees: number;
  tax: number;
  currency: string;
  invoiceNumber: string;
  dueDate: string;
  dateOfIssue: string;
  description?: string;
  collectionMethod: string;
}

export interface StripePlanInfo {
  interval: string;
  intervalCount: number;
}

/**
 * Create stripe plan info from billing period
 * @param billing period i.e. day, week, month and year
 * @returns plan for subscription invoice, undefined otherwise
 */
export const getStripePlanInfoFromBillingPeriod = (billingPeriod: string) => {
  switch (billingPeriod) {
    case BILLING_INTERVALS.QUARTERLY: {
      return { interval: BILLING_INTERVALS.MONTH, intervalCount: 3 };
    }
    case BILLING_INTERVALS.BIANNUALLY: {
      return { interval: BILLING_INTERVALS.MONTH, intervalCount: 6 };
    }
    default: {
      return { interval: billingPeriod, intervalCount: 1 };
    }
  }
};

export const getPaymentMethodTypeFromOptions = (
  options: InvoicePaymentOptions,
) => {
  if (options.allowPaymentViaACH && !options.allowPaymentViaCC) {
    return 'bank_account';
  }
  if (options.allowPaymentViaCC && !options.allowPaymentViaACH) {
    return 'card';
  }

  return 'unknown';
};

interface BillingRecipient {
  data: RecipientDataModel;
}

export const billingRecipientComparator = (
  _a: any,
  _b: any,
  nodeA: BillingRecipient,
  nodeB: BillingRecipient,
) => {
  const { companyFields: aCompanyFields, client: aClient } = nodeA.data;
  const { companyFields: bCompanyFields, client: bClient } = nodeB.data;
  const a: string =
    aClient && aClient.fields
      ? `${aClient.fields.given_name}${aClient.fields.family_name}`
      : aCompanyFields?.fields.name || '';
  const b: string =
    bClient && bClient.fields
      ? `${bClient.fields.given_name}${bClient.fields.family_name}`
      : bCompanyFields?.fields.name || '';
  return a.localeCompare(b);
};
